"use client";

import { c as _c } from "react/compiler-runtime";
import { TokenIcon } from "@/components/ui/token-icon";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { formatTokenAmount } from "../../utils/formatters";
import { containsTemplateVariable, renderValueWithVariables } from "../VariablePill";
interface DexSwapPayloadProps {
  tokenIn?: string;
  tokenOut?: string;
  amountIn?: string | number;
  chainId?: number;
  deadlineMinutes?: number;
  slippageBps?: number;
}

/**
 * DexSwapPayload - Compact display for V2 DEX Swap actions
 * Shows: Swap [amount] [tokenIn icon] [tokenIn symbol] for [tokenOut icon] [tokenOut symbol] on [chain icon] [chain name]
 */
export function DexSwapPayload(t0) {
  const $ = _c(42);
  const {
    tokenIn,
    tokenOut,
    amountIn,
    chainId,
    deadlineMinutes,
    slippageBps
  } = t0;
  const shouldFetchTokenIn = !!(tokenIn && chainId && tokenIn.startsWith("0x"));
  const shouldFetchTokenOut = !!(tokenOut && chainId && tokenOut.startsWith("0x"));
  const {
    data: simTokenInData
  } = useSimTokenInfo(tokenIn, chainId, shouldFetchTokenIn);
  const {
    data: simTokenOutData
  } = useSimTokenInfo(tokenOut, chainId, shouldFetchTokenOut);
  let t1;
  if ($[0] !== chainId) {
    t1 = chainId ? [chainId] : [];
    $[0] = chainId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: chainInfoMap
  } = useChainInfoMap(t1);
  const tokenInInfo = simTokenInData?.tokens?.[0];
  const tokenOutInfo = simTokenOutData?.tokens?.[0];
  let t2;
  if ($[2] !== chainId || $[3] !== chainInfoMap) {
    t2 = chainId ? chainInfoMap?.get(chainId) : undefined;
    $[2] = chainId;
    $[3] = chainInfoMap;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const chain = t2;
  const tokenInLogo = tokenInInfo?.logo || tokenInInfo?.token_metadata?.logo;
  const tokenOutLogo = tokenOutInfo?.logo || tokenOutInfo?.token_metadata?.logo;
  let t3;
  if ($[5] !== amountIn) {
    t3 = containsTemplateVariable(amountIn);
    $[5] = amountIn;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const amountIsVariable = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span>Swap</span>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== amountIn || $[9] !== amountIsVariable || $[10] !== tokenInInfo?.decimals) {
    t5 = amountIsVariable ? renderValueWithVariables(amountIn) : <span className="text-ds-text-primary font-semibold">{formatTokenAmount(amountIn, tokenInInfo?.decimals)}</span>;
    $[8] = amountIn;
    $[9] = amountIsVariable;
    $[10] = tokenInInfo?.decimals;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== chainId || $[13] !== tokenIn || $[14] !== tokenInInfo || $[15] !== tokenInLogo) {
    t6 = tokenInInfo && <><TokenIcon logoUrl={tokenInLogo} symbol={tokenInInfo.symbol} chainId={chainId} tokenAddress={tokenIn} size={16} className="flex-shrink-0" /><span className="text-ds-text-primary font-semibold">{tokenInInfo.symbol}</span></>;
    $[12] = chainId;
    $[13] = tokenIn;
    $[14] = tokenInInfo;
    $[15] = tokenInLogo;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span>for</span>;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== chainId || $[19] !== tokenOut || $[20] !== tokenOutInfo || $[21] !== tokenOutLogo) {
    t8 = tokenOutInfo && <><TokenIcon logoUrl={tokenOutLogo} symbol={tokenOutInfo.symbol} chainId={chainId} tokenAddress={tokenOut} size={16} className="flex-shrink-0" /><span className="text-ds-text-primary font-semibold">{tokenOutInfo.symbol}</span></>;
    $[18] = chainId;
    $[19] = tokenOut;
    $[20] = tokenOutInfo;
    $[21] = tokenOutLogo;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span>on</span>;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== chain) {
    t10 = chain?.iconUrl && <img src={chain.iconUrl} alt={chain.name} className="h-4 w-4 rounded-full" />;
    $[24] = chain;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = chain?.name || `Chain ${chainId}`;
  let t12;
  if ($[26] !== t11) {
    t12 = <span className="text-ds-text-primary font-medium">{t11}</span>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t12 || $[30] !== t5 || $[31] !== t6 || $[32] !== t8) {
    t13 = <div className="text-ds-text-secondary flex flex-wrap items-center gap-1.5 text-xs">{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t12}</div>;
    $[28] = t10;
    $[29] = t12;
    $[30] = t5;
    $[31] = t6;
    $[32] = t8;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== deadlineMinutes) {
    t14 = deadlineMinutes !== undefined && <div className="flex items-start gap-2 text-xs"><span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">Deadline Minutes:</span><span className="text-ds-text-primary font-mono">{deadlineMinutes}</span></div>;
    $[34] = deadlineMinutes;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== slippageBps) {
    t15 = slippageBps !== undefined && <div className="flex items-start gap-2 text-xs"><span className="text-ds-text-muted dark:text-ds-text-muted whitespace-nowrap font-medium">Slippage Bps:</span><span className="text-ds-text-primary font-mono">{slippageBps}</span></div>;
    $[36] = slippageBps;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t13 || $[39] !== t14 || $[40] !== t15) {
    t16 = <div className="border-ds-border-default space-y-2 border-t pt-3">{t13}{t14}{t15}</div>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
}